import { ModalHeader, Typography } from '@execonline-inc/execonline-ui';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { assertNever } from '@kofno/piper';
import clsx from 'clsx';
import { Maybe } from 'maybeasy';
import * as React from 'react';
import { T } from '../../../../../Translations';
import { AlertSeverity } from './Selectors';

interface Props {
  severity: AlertSeverity;
  withGaps: number;
  total: number;
  // When one person was added, the copy speaks about them by name.
  sole: Maybe<{ firstName: string; enrollable: number }>;
  totalCourses: number;
}

// Every state carries a badge, the good news included: the modal is the same
// alert the banner is, and a title with no icon beside one with an icon reads
// as two different components rather than two states of one.
const badgeIcon = (severity: AlertSeverity): HeroIcon => {
  switch (severity) {
    case 'clear':
      return CheckCircleIcon;
    case 'mixed':
    case 'limited':
    case 'blocked':
      return ExclamationTriangleIcon;
    default:
      return assertNever(severity);
  }
};

// The theme carries no warning token - the banner's amber lives inside the
// design system's own Alert - so caution reads as primary rather than as a
// hardcoded amber. Clear takes the same accent green the fully licensed groups
// already use, and only blocked claims destructive.
const badgeClassName = (severity: AlertSeverity): string => {
  switch (severity) {
    case 'clear':
      return 'bg-accent/10';
    case 'blocked':
      return 'bg-destructive/10';
    case 'mixed':
    case 'limited':
      return 'bg-primary/10';
    default:
      return assertNever(severity);
  }
};

const badgeIconClassName = (severity: AlertSeverity): string => {
  switch (severity) {
    case 'clear':
      return 'text-accent';
    case 'blocked':
      return 'text-destructive';
    case 'mixed':
    case 'limited':
      return 'text-primary';
    default:
      return assertNever(severity);
  }
};

type HeroIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

function Header({ severity, withGaps, total, sole, totalCourses }: Props) {
  const BadgeIcon = badgeIcon(severity);
  const title: React.ReactNode = sole.cata<React.ReactNode>({
    Just: (person) => (
      <T
        kind="{{name}} can enroll in {{enrollable}} of {{total}} courses"
        name={person.firstName}
        enrollable={person.enrollable}
        total={totalCourses}
      />
    ),
    Nothing: () => {
      switch (severity) {
        case 'clear':
          return <T kind="Everyone is fully licensed" />;
        case 'mixed':
          return withGaps === 1 ? (
            <T
              kind="{{withGaps}} of {{total}} people isn't fully licensed"
              withGaps={withGaps}
              total={total}
            />
          ) : (
            <T
              kind="{{withGaps}} of {{total}} people aren't fully licensed"
              withGaps={withGaps}
              total={total}
            />
          );
        case 'limited':
          return <T kind="No one is fully licensed" />;
        case 'blocked':
          return <T kind="No one is licensed for these courses" />;
        default:
          return assertNever(severity);
      }
    },
  });

  return (
    <ModalHeader
      className="w-full items-start gap-3"
      data-intercom-target="share-license-alerts-header"
    >
      <div
        className={clsx(
          'flex h-10 w-10 flex-none items-center justify-center rounded-full',
          badgeClassName(severity),
        )}
      >
        <BadgeIcon className={clsx('h-5 w-5', badgeIconClassName(severity))} aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <Typography variant="subtitleLarge" fontWeight="semibold">
          {title}
        </Typography>
        <Typography variant="bodySmall" className="mt-1 text-muted-foreground">
          <T kind="Licenses decide which courses someone can enroll in." />
        </Typography>
      </div>
    </ModalHeader>
  );
}

export default Header;
